import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { PanelRight, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { TicketSidebar } from '../../components/tickets/TicketSidebar';
import { TicketList } from '../../components/tickets/TicketList';
import { TicketDetailPanel } from '../../components/tickets/TicketDetailPanel';
import { CustomerContextPanel } from '../../components/tickets/CustomerContextPanel';
import {
  Ticket,
  TicketDetail,
  getTickets,
  getTicketDetail,
  getTicketsCount,
  updateTicketStatus,
  snoozeTicket,
  unsnoozeTicket,
  addTicketMessage,
  TicketAttachment,
  updateTicketTags,
  assignTicket,
  backfillTickets,
  getAgents,
} from '../../services/api';

// Module-level cache for tickets (persists across navigation)
let _cachedTickets: Ticket[] | null = null;
let _ticketsCacheTimestamp = 0;
const TICKETS_CACHE_TTL = 3 * 60 * 1000; // 3 minutes
const POLL_INTERVAL = 30 * 1000; // 30 seconds

export function InboxPageNew() {
  const [tickets, setTickets] = useState<Ticket[]>(_cachedTickets || []);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [selectedTicketId, setSelectedTicketId] = useState<string | undefined>(undefined);
  const [selectedTicketDetail, setSelectedTicketDetail] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [newTicketCount, setNewTicketCount] = useState(0);

  // Filters
  const [selectedView, setSelectedView] = useState('inbox');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string | undefined>();

  // Sidebar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Customer context panel
  const [contextPanelOpen, setContextPanelOpen] = useState(true);

  // Agents for assignment
  const [agentOptions, setAgentOptions] = useState<Array<{ id: string; name: string }>>([]);

  // Polling
  const lastFetchRef = useRef<string>(new Date().toISOString());

  // Load agents on mount
  useEffect(() => {
    getAgents()
      .then(res => {
        const options = (res.agents || []).map(a => ({ id: a.agent_id || a.id, name: a.name }));
        setAgentOptions(options);
      })
      .catch(() => { /* agents optional */ });
  }, []);

  // Load tickets on mount (with cache)
  useEffect(() => {
    const hasFreshCache = _cachedTickets && (Date.now() - _ticketsCacheTimestamp < TICKETS_CACHE_TTL);
    if (hasFreshCache) {
      setTickets(_cachedTickets!);
      // Background revalidate without spinner
      loadTickets(false);
    } else {
      loadTickets(true);
    }
  }, []);

  // Poll for new tickets every 30 seconds
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const result = await getTicketsCount(lastFetchRef.current);
        if (result.count > 0) {
          setNewTicketCount(result.count);
          toast(`${result.count} new ticket${result.count > 1 ? 's' : ''} available`, {
            icon: '\uD83D\uDD14',
            duration: 5000,
          });
          // Auto-refresh ticket list in background
          loadTickets(false);
        }
      } catch {
        // Polling errors are non-critical, silently ignore
      }
    }, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Apply filters when tickets or filters change
  useEffect(() => {
    applyFilters();
  }, [tickets, selectedView, selectedTags, selectedChannel, searchQuery]);

  // Fetch ticket detail when selection changes
  useEffect(() => {
    if (selectedTicketId) {
      loadTicketDetail(selectedTicketId);
    } else {
      setSelectedTicketDetail(null);
    }
  }, [selectedTicketId]);

  async function loadTickets(showLoading = true) {
    if (showLoading) setLoading(true);
    try {
      const response = await getTickets();
      const ticketList = response.tickets || [];
      setTickets(ticketList);
      setNewTicketCount(0);
      // Update cache and polling timestamp
      _cachedTickets = ticketList;
      _ticketsCacheTimestamp = Date.now();
      lastFetchRef.current = new Date().toISOString();
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadTicketDetail(ticketId: string) {
    setDetailLoading(true);
    try {
      const detail = await getTicketDetail(ticketId);
      setSelectedTicketDetail(detail);
    } catch (error) {
      console.error('Error loading ticket detail:', error);
      setSelectedTicketDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  function applyFilters() {
    let filtered = [...tickets];

    // Apply view filter
    const now = new Date();
    if (selectedView === 'inbox') {
      filtered = filtered.filter(t =>
        (t.status === 'open' || t.status === 'pending') &&
        (!t.snoozed_until || new Date(t.snoozed_until) <= now)
      );
    } else if (selectedView === 'closed') {
      filtered = filtered.filter(t => t.status === 'resolved' || t.status === 'closed');
    } else if (selectedView === 'snoozed') {
      filtered = filtered.filter(t =>
        t.snoozed_until && new Date(t.snoozed_until) > now
      );
    } else if (selectedView === 'needs_response') {
      filtered = filtered.filter(t =>
        t.status === 'open' &&
        (t.response_count === 0 || t.response_count === undefined) &&
        (!t.snoozed_until || new Date(t.snoozed_until) <= now)
      );
    } else if (selectedView === 'negative_sentiment') {
      filtered = filtered.filter(t =>
        t.sentiment === 'negative' && t.status !== 'closed' && t.status !== 'resolved'
      );
    } else if (selectedView === 'flagged_review') {
      filtered = filtered.filter(t =>
        t.requires_human_review === true && t.status !== 'closed' && t.status !== 'resolved'
      );
    }
    // 'all' = no status filter

    // Apply tags filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(ticket =>
        ticket.tags?.some(tag => selectedTags.includes(tag))
      );
    }

    // Apply channel filter
    if (selectedChannel) {
      filtered = filtered.filter(t => t.channel === selectedChannel);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ticket =>
        ticket.subject?.toLowerCase().includes(query) ||
        ticket.customer_email?.toLowerCase().includes(query) ||
        ticket.customer_name?.toLowerCase().includes(query) ||
        ticket.ticket_id?.toLowerCase().includes(query)
      );
    }

    setFilteredTickets(filtered);
  }

  // Compute view counts from all tickets (including AI smart views)
  const viewCounts = useMemo(() => {
    const now = new Date();
    const openTickets = tickets.filter(t =>
      (t.status === 'open' || t.status === 'pending') &&
      (!t.snoozed_until || new Date(t.snoozed_until) <= now)
    );
    return {
      inbox: openTickets.length,
      all: tickets.length,
      snoozed: tickets.filter(t =>
        t.snoozed_until && new Date(t.snoozed_until) > now
      ).length,
      closed: tickets.filter(t =>
        t.status === 'resolved' || t.status === 'closed'
      ).length,
      // AI Smart Views
      needs_response: openTickets.filter(t =>
        t.status === 'open' && (t.response_count === 0 || t.response_count === undefined)
      ).length,
      negative_sentiment: tickets.filter(t =>
        t.sentiment === 'negative' && t.status !== 'closed' && t.status !== 'resolved'
      ).length,
      flagged_review: tickets.filter(t =>
        t.requires_human_review === true && t.status !== 'closed' && t.status !== 'resolved'
      ).length,
    };
  }, [tickets]);

  // Collect unique tags from ticket data
  const availableTags = useMemo(() => {
    const tagSet = new Set<string>();
    tickets.forEach(t => t.tags?.forEach(tag => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [tickets]);

  // Action handlers
  const handleStatusChange = useCallback(async (ticketId: string, status: string) => {
    try {
      await updateTicketStatus(ticketId, status);
      toast.success(`Ticket marked as ${status}`);
      await loadTickets();
      if (selectedTicketId === ticketId) {
        await loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Error updating status:', error);
    }
  }, [selectedTicketId]);

  const handleSnooze = useCallback(async (ticketId: string, snoozeUntil: string | null) => {
    try {
      if (snoozeUntil) {
        await snoozeTicket(ticketId, snoozeUntil);
        toast.success('Ticket snoozed');
      } else {
        await unsnoozeTicket(ticketId);
        toast.success('Ticket unsnoozed');
      }
      await loadTickets();
      if (selectedTicketId === ticketId) {
        await loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to snooze ticket');
      console.error('Error snoozing ticket:', error);
    }
  }, [selectedTicketId]);

  const handleAddMessage = useCallback(async (ticketId: string, content: string, type: 'email' | 'note' = 'note', isDraft?: boolean, attachments?: TicketAttachment[]) => {
    await addTicketMessage(ticketId, {
      content,
      role: 'agent',
      author_name: 'Agent',
      type,
      is_draft: isDraft,
      attachments,
    });
    loadTickets();
    if (selectedTicketId === ticketId) {
      await loadTicketDetail(ticketId);
    }
  }, [selectedTicketId]);

  const handleTagsChange = useCallback(async (ticketId: string, tags: string[]) => {
    try {
      await updateTicketTags(ticketId, tags);
      toast.success('Tags updated');
      await loadTickets();
      if (selectedTicketId === ticketId) {
        await loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to update tags');
      console.error('Error updating tags:', error);
    }
  }, [selectedTicketId]);

  const handleAssign = useCallback(async (ticketId: string, agentId: string, agentName: string) => {
    try {
      await assignTicket(ticketId, agentId, agentName || undefined);
      toast.success(agentName ? `Assigned to ${agentName}` : 'Unassigned');
      await loadTickets();
      if (selectedTicketId === ticketId) {
        await loadTicketDetail(ticketId);
      }
    } catch (error) {
      toast.error('Failed to assign ticket');
      console.error('Error assigning ticket:', error);
    }
  }, [selectedTicketId]);

  function handleViewChange(viewId: string) {
    setSelectedView(viewId);
    setSelectedTicketId(undefined);
  }

  function handleTagToggle(tag: string) {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  }

  function handleChannelChange(channel: string) {
    setSelectedChannel(selectedChannel === channel ? undefined : channel);
  }

  function handleTicketSelect(ticketId: string) {
    setSelectedTicketId(ticketId);
  }

  function handleSearchChange(query: string) {
    setSearchQuery(query);
  }

  function handleRefresh() {
    loadTickets();
  }

  async function handleSync() {
    setSyncing(true);
    try {
      const result = await backfillTickets();
      if (result.created > 0) {
        toast.success(`Synced ${result.created} new ticket${result.created > 1 ? 's' : ''}`);
      } else {
        toast('All tickets already synced', { icon: '\u2713' });
      }
      await loadTickets();
    } catch (error) {
      toast.error('Failed to sync tickets');
      console.error('Error syncing tickets:', error);
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Left Sidebar - Filters & Views */}
      <TicketSidebar
        selectedView={selectedView}
        onViewChange={handleViewChange}
        selectedTags={selectedTags}
        onTagToggle={handleTagToggle}
        selectedChannel={selectedChannel}
        onChannelChange={handleChannelChange}
        viewCounts={viewCounts}
        availableTags={availableTags}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      {/* Middle Panel - Ticket List */}
      <div className="w-96 flex-shrink-0">
        <TicketList
          tickets={filteredTickets}
          selectedTicketId={selectedTicketId}
          onTicketSelect={handleTicketSelect}
          loading={loading}
          onRefresh={handleRefresh}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSync={handleSync}
          syncing={syncing}
        />
      </div>

      {/* Right Panel - Ticket Details */}
      <TicketDetailPanel
        ticketDetail={selectedTicketDetail}
        loading={detailLoading}
        agents={agentOptions}
        onStatusChange={handleStatusChange}
        onSnooze={handleSnooze}
        onAddMessage={handleAddMessage}
        onAssign={handleAssign}
        onTagsChange={handleTagsChange}
        onClose={() => setSelectedTicketId(undefined)}
      />

      {/* Customer Context Panel - Right Side */}
      {selectedTicketDetail && contextPanelOpen && (
        <div className="hidden xl:flex">
          <CustomerContextPanel
            customerEmail={selectedTicketDetail.ticket.customer_email}
            customerName={selectedTicketDetail.ticket.customer_name}
            onCollapse={() => setContextPanelOpen(false)}
          />
        </div>
      )}

      {/* Collapsed context panel toggle */}
      {selectedTicketDetail && !contextPanelOpen && (
        <button
          onClick={() => setContextPanelOpen(true)}
          className="hidden xl:flex flex-shrink-0 w-10 bg-white border-l border-gray-200/60 flex-col items-center pt-3 gap-2 hover:bg-gray-50 transition-colors"
          title="Show customer panel"
        >
          <User size={14} className="text-gray-400" />
          <PanelRight size={14} className="text-gray-400" />
        </button>
      )}

    </div>
  );
}
